import { Service, ServiceDependencyGroup, DependencyOperator, UserCertificateApproved } from "@/types";

/**
 * Converts legacy dependentServiceIds array to new dependencyGroups format
 * Creates a single AND group with all services (user must complete ALL)
 */
export function convertLegacyDependencies(
  dependentServiceIds: string[]
): ServiceDependencyGroup[] {
  if (!dependentServiceIds || dependentServiceIds.length === 0) {
    return [];
  }

  // Convert to single AND group - user must complete ALL services
  return [
    {
      operator: "AND",
      dependentServiceIds: dependentServiceIds,
    },
  ];
}

/**
 * Formats dependency groups as a human-readable string
 * Example: "(Service A OR Service B) AND Service C"
 */
export function formatDependencyGroups(service: Service): string {
  // Use dependencyGroups if available, otherwise fallback to legacy
  const groups = service.dependencyGroups || 
    (service.dependentServices && service.dependentServices.length > 0
      ? convertLegacyDependencies(service.dependentServices.map(s => s.id))
      : []);

  if (groups.length === 0) {
    return "No prerequisites";
  }

  const formattedGroups = groups.map((group) => {
    const serviceNames = group.dependentServices
      ? group.dependentServices.map((s) => s.name)
      : group.dependentServiceIds.map((id) => {
          // Try to find service name from dependentServices array
          const foundService = service.dependentServices?.find((s: Service) => s.id === id);
          return foundService?.name || id;
        });

    if (serviceNames.length === 0) {
      return "";
    }

    if (serviceNames.length === 1) {
      return serviceNames[0];
    }

    // Multiple services in group = OR logic
    return `(${serviceNames.join(" OR ")})`;
  }).filter(Boolean);

  if (formattedGroups.length === 0) {
    return "No prerequisites";
  }

  // Combine groups with AND operator (default)
  // Note: The operator field in groups is for combining groups, but default is AND
  return formattedGroups.join(" AND ");
}

/**
 * Checks if a user has completed the required services for a dependency group
 * Returns true if user has completed ANY service in the group (OR logic)
 * When API returns only dependentServiceIds (no dependentServices), servicesMap resolves id -> name
 */
function checkGroupMet(
  group: ServiceDependencyGroup,
  userCertificates: UserCertificateApproved[],
  servicesMap?: Map<string, string>
): boolean {
  if (group.dependentServiceIds.length === 0) {
    return true;
  }

  return group.dependentServiceIds.some((serviceId) => {
    const serviceName = group.dependentServices?.find((s: Service) => s.id === serviceId)?.name
      ?? servicesMap?.get(serviceId);
    if (!serviceName) {
      return false;
    }
    return userCertificates.some(
      (certificate) =>
        certificate.applicationName?.toLowerCase() === serviceName.toLowerCase()
    );
  });
}

/**
 * Checks if user meets all dependency requirements for a service
 * Handles both new dependencyGroups and legacy dependentServices
 * allServices: optional list to resolve dependentServiceIds to names when API returns only IDs
 */
export function checkDependencyGroupsMet(
  service: Service,
  userCertificates: UserCertificateApproved[],
  allServices?: Service[]
): boolean {
  const servicesMap = allServices?.length
    ? new Map(allServices.map((s) => [s.id, s.name]))
    : undefined;

  if (service.dependencyGroups && service.dependencyGroups.length > 0) {
    const groupResults = service.dependencyGroups.map((group) =>
      checkGroupMet(group, userCertificates, servicesMap)
    );
    return groupResults.every((met) => met);
  }

  // Fallback to legacy dependentServices (all must be completed - AND logic)
  if (service.dependentServices && service.dependentServices.length > 0) {
    return service.dependentServices.every((depService) => {
      return userCertificates.some(
        (certificate) =>
          certificate.applicationName.toLowerCase() ===
          depService.name.toLowerCase()
      );
    });
  }

  // No dependencies = always met
  return true;
}

/**
 * Gets the names of missing dependencies for a service
 * Returns formatted string showing what's missing
 */
export function getMissingDependencies(
  service: Service,
  userCertificates: UserCertificateApproved[],
  allServices?: Service[]
): string {
  const servicesMap = allServices?.length
    ? new Map(allServices.map((s) => [s.id, s.name]))
    : undefined;

  if (service.dependencyGroups && service.dependencyGroups.length > 0) {
    const missingGroups: string[] = [];

    service.dependencyGroups.forEach((group) => {
      const hasCompleted = group.dependentServiceIds.some((serviceId) => {
        const serviceName = group.dependentServices?.find((s: Service) => s.id === serviceId)?.name
          ?? servicesMap?.get(serviceId);
        if (!serviceName) return false;
        return userCertificates.some(
          (certificate) =>
            certificate.applicationName?.toLowerCase() === serviceName.toLowerCase()
        );
      });

      if (!hasCompleted) {
        const serviceNames = group.dependentServiceIds.map((id) =>
          group.dependentServices?.find((s: Service) => s.id === id)?.name ?? servicesMap?.get(id) ?? id
        );
        if (serviceNames.length === 1) {
          missingGroups.push(serviceNames[0]);
        } else if (serviceNames.length > 1) {
          missingGroups.push(`(${serviceNames.join(" OR ")})`);
        }
      }
    });

    if (missingGroups.length === 0) {
      return "";
    }

    return missingGroups.join(" AND ");
  }

  // Fallback to legacy dependentServices
  if (service.dependentServices && service.dependentServices.length > 0) {
    const missing = service.dependentServices.filter((depService) => {
      return !userCertificates.some(
        (certificate) =>
          certificate.applicationName.toLowerCase() ===
          depService.name.toLowerCase()
      );
    });

    if (missing.length === 0) {
      return "";
    }

    return missing.map((s) => s.name).join(", ");
  }

  return "";
}

/**
 * Gets all dependency service IDs from a service (from both new and legacy structures)
 */
export function getAllDependencyServiceIds(service: Service): string[] {
  const ids: string[] = [];

  // Add from dependencyGroups
  if (service.dependencyGroups) {
    service.dependencyGroups.forEach((group) => {
      ids.push(...group.dependentServiceIds);
    });
  }

  // Add from legacy dependentServices
  if (service.dependentServices && service.dependentServices.length > 0) {
    const legacyIds = service.dependentServices.map((s) => s.id);
    legacyIds.forEach((id) => {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    });
  }

  // Add from legacy dependentServiceIds (if present in request)
  if (service.dependentServiceIds) {
    service.dependentServiceIds.forEach((id) => {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    });
  }

  return ids;
}

